import React, { useMemo, useState } from 'react';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useMutation, useQuery } from 'react-query';

import { Row, Stack } from '@raptormaps/layout';
import { Text } from '@raptormaps/text';
import { RadioGroup } from '@raptormaps/radio-group';
import { Button } from '@raptormaps/button';
import { useToast } from '@raptormaps/toast';
import {
  Modal,
  ModalClose,
  ModalCloseButton,
  ModalContent,
  ModalTrigger,
  ModalTitle,
} from '@raptormaps/modal';

import {
  getFarmOrgsQuery,
  getAddOrgAccessToFarmMutation,
} from '../../api/services/farm-orgs';

interface AddOrgToFarmProps {
  solarFarmId: number;
  solarFarmOrgId: number;
  orgNameMap: Map<number, string>;
}

export const AddOrgToFarm = ({
  solarFarmId,
  solarFarmOrgId,
  orgNameMap,
}: AddOrgToFarmProps) => {
  const toast = useToast();
  const [open, setOpen] = useState<boolean>(false);

  const { data: orgIdsAlreadyWithPermission, isLoading } = useQuery({
    ...getFarmOrgsQuery(solarFarmId, solarFarmOrgId),
    select: response => response.data.map(entry => entry.user_id),
  });

  const { mutateAsync: addOrgAccessToFarm, isLoading: isSubmitting } =
    useMutation(getAddOrgAccessToFarmMutation(solarFarmId, solarFarmOrgId));

  const orgOptions = useMemo(() => {
    return Array.from(orgNameMap, ([orgId, orgName]) => ({
      id: orgId,
      name: orgName,
    })).filter(
      option => orgIdsAlreadyWithPermission?.includes(option.id) === false,
    );
  }, [orgNameMap, orgIdsAlreadyWithPermission]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const orgNameToAddFormEntry = formData.get('org-to-add');
    const editAccessFormEntry = formData.get('edit-access');

    const orgIdToAdd = orgOptions.find(
      option => option.name === orgNameToAddFormEntry,
    )?.id;
    const canEdit = editAccessFormEntry === 'yes';

    if (!orgIdToAdd) {
      // this should be impossible, but just in case
      throw new Error(`Invalid option - ${orgNameToAddFormEntry}`);
    }

    toast.promise(
      addOrgAccessToFarm({ orgIdToAdd, canEdit }).then(() => setOpen(false)),
      {
        loading: 'Adding org access to farm...',
        error: 'Unable to give org access to the farm. Please try again.',
        success: 'Org access to farm has been added!',
      },
    );
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <ModalTrigger onClick={() => setOpen(true)}>
        Add Organization
      </ModalTrigger>
      <ModalContent>
        <ModalCloseButton />
        <Stack
          as="form"
          onSubmit={handleSubmit}
          gutter="xl"
          className="remove_label_margin"
        >
          <ModalTitle>Add Organization to Site</ModalTitle>
          <Text>
            Once you add a new org, all org members will be notified via email.
          </Text>
          <Stack gutter="xs">
            <Text as="label" htmlFor="org-to-add">
              Please select an organization
            </Text>
            <Typeahead
              options={orgOptions}
              labelKey="name"
              id="org-to-add"
              isLoading={isLoading}
              inputProps={{ id: 'org-to-add', name: 'org-to-add' }}
              disabled={isSubmitting}
            />
          </Stack>
          <Stack gutter="xs">
            <Text as="label" htmlFor="edit-access">
              Edit Access
            </Text>
            <RadioGroup
              id="edit-access"
              name="edit-access"
              disabled={isSubmitting}
              defaultValue="no"
              items={[
                { label: 'On', value: 'yes' },
                { label: 'Off', value: 'no' },
              ]}
            />
          </Stack>
          <Row>
            <ModalClose asChild>
              <Button variant="secondary" flex disabled={isSubmitting}>
                Cancel
              </Button>
            </ModalClose>
            <Button type="submit" flex disabled={isSubmitting}>
              Add Organization
            </Button>
          </Row>
        </Stack>
      </ModalContent>
    </Modal>
  );
};
